import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SearchSelect from "../components/SearchSelect";
import CompanySearchSelect from "../components/companies/CompanySearchSelect";
import ContactFormDialog from "../components/contacts/ContactFormDialog";
import { useAuth } from "../context/AuthContext";
import { Plus, Pencil, Trash2, CheckSquare, ListChecks, Eye } from "lucide-react";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

export default function Deals() {
    const { user, hasPermission } = useAuth();
    const canManage = hasPermission("deals_manage");
    const canCreateContact = hasPermission("contacts_manage");
    const canCreateCompany = hasPermission("companies_manage");
    const canManageTasks = hasPermission("tasks_manage");
    const [funnels, setFunnels] = useState([]);
    const [funnelId, setFunnelId] = useState("");
    const [deals, setDeals] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [open, setOpen] = useState(false);
    const [viewing, setViewing] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});
    const [contactOpen, setContactOpen] = useState(false);
    const [users, setUsers] = useState([]);

    // Tasks linked to currently-open deal
    const [dealTasks, setDealTasks] = useState([]);
    // Quick-add task modal
    const [taskOpen, setTaskOpen] = useState(false);
    const [taskForm, setTaskForm] = useState({});
    // Inline task section embedded in the "New Deal" dialog
    const [inlineTask, setInlineTask] = useState({ enabled: false, title: "", due_date: "", type: "tarea", priority: "media" });

    const reloadDeals = async (fid) => {
        const { data } = await api.get("/deals", { params: { funnel_id: fid } });
        setDeals(data);
    };

    const load = async () => {
        const [fn, co, le, us] = await Promise.all([
            api.get("/funnels"),
            api.get("/companies"),
            api.get("/leads"),
            api.get("/admin/users/lite").catch(() => ({ data: [] })),
        ]);
        setFunnels(fn.data);
        setCompanies(co.data);
        setContacts(le.data);
        setUsers(us.data || []);
        const fid = funnelId || fn.data[0]?.funnel_id;
        if (fid) {
            setFunnelId(fid);
            await reloadDeals(fid);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    useEffect(() => { if (funnelId) reloadDeals(funnelId); /* eslint-disable-next-line */ }, [funnelId]);

    const currentFunnel = funnels.find(f => f.funnel_id === funnelId);
    const stages = (currentFunnel?.stages || []).slice().sort((a,b)=>a.order-b.order);
    const companyName = (companyId) => companies.find((company) => company.company_id === companyId)?.name || "Sin empresa";
    const contactName = (contactId) => {
        const contact = contacts.find((item) => item.lead_id === contactId);
        if (!contact) return "Sin contacto";
        return `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || contact.email || "Sin contacto";
    };
    const stageName = (stageId) => stages.find((stage) => stage.stage_id === stageId)?.name || "Sin etapa";

    const onDragEnd = async (result) => {
        if (!canManage || !result.destination) return;
        const dealId = result.draggableId;
        const newStageId = result.destination.droppableId;
        setDeals(prev => prev.map(d => d.deal_id === dealId ? { ...d, stage_id: newStageId } : d));
        try { await api.post(`/deals/${dealId}/move`, { stage_id: newStageId }); }
        catch (e) { toast.error("No se pudo mover"); load(); }
    };

    const openNew = (stageId) => {
        setViewing(null);
        setEditing(null);
        setForm({ stage_id: stageId, funnel_id: funnelId, currency: "USD", priority: "media", amount: 0 });
        setDealTasks([]);
        setInlineTask({ enabled: false, title: "", due_date: "", type: "tarea", priority: "media" });
        setOpen(true);
    };

    const loadDealTasks = async (dealId) => {
        try {
            const { data } = await api.get("/tasks", { params: { deal_id: dealId } });
            setDealTasks(data);
        } catch {
            setDealTasks([]);
        }
    };

    const openView = async (deal) => {
        setEditing(null);
        setDealTasks([]);
        setViewing(deal);
        await loadDealTasks(deal.deal_id);
    };

    const openEdit = async (deal) => {
        setViewing(null);
        setEditing(deal);
        setForm({ ...deal });
        setDealTasks([]);
        setOpen(true);
        await loadDealTasks(deal.deal_id);
    };

    const save = async () => {
        try {
            if (editing) {
                await api.patch(`/deals/${editing.deal_id}`, form);
                toast.success("Deal actualizado");
            } else {
                const { data: deal } = await api.post("/deals", form);
                // If the user opted to create an initial task, attach it here.
                if (inlineTask.enabled && inlineTask.title.trim()) {
                    try {
                        await api.post("/tasks", {
                            title: inlineTask.title.trim(),
                            type: inlineTask.type || "tarea",
                            priority: inlineTask.priority || "media",
                            status: "pendiente",
                            deal_id: deal.deal_id,
                            contact_id: form.contact_id,
                            company_id: form.company_id,
                            due_date: inlineTask.due_date ? new Date(inlineTask.due_date).toISOString() : undefined,
                        });
                        toast.success("Deal y tarea creados");
                    } catch (e2) {
                        toast.error("Deal creado, pero no se pudo crear la tarea");
                    }
                } else {
                    toast.success("Deal creado");
                }
            }
            setOpen(false);
            await reloadDeals(funnelId);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const removeDeal = async (deal) => {
        if (!window.confirm(`¿Eliminar el deal "${deal.title}"?`)) return;
        try {
            await api.delete(`/deals/${deal.deal_id}`);
            toast.success("Deal eliminado");
            setOpen(false);
            setViewing(null);
            setEditing(null);
            await reloadDeals(funnelId);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    // ---- Quick task add (from open deal) ----
    const openNewTask = () => {
        setTaskForm({
            type: "tarea", priority: "media",
            deal_id: editing?.deal_id,
            contact_id: editing?.contact_id,
            company_id: editing?.company_id,
        });
        setTaskOpen(true);
    };

    const saveTask = async () => {
        try {
            const payload = { ...taskForm };
            if (payload.due_date) payload.due_date = new Date(payload.due_date).toISOString();
            await api.post("/tasks", payload);
            toast.success("Tarea creada");
            setTaskOpen(false);
            if (editing) {
                const { data } = await api.get("/tasks", { params: { deal_id: editing.deal_id } });
                setDealTasks(data);
            }
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const toggleTask = async (t) => {
        await api.patch(`/tasks/${t.task_id}`, { completed: !t.completed });
        if (editing) {
            const { data } = await api.get("/tasks", { params: { deal_id: editing.deal_id } });
            setDealTasks(data);
        }
    };

    return (
        <Layout
            title="Pipeline de Deals"
            subtitle="Arrastra entre etapas para actualizar"
            actions={
                <>
                    <Select value={funnelId} onValueChange={setFunnelId}>
                        <SelectTrigger className="w-56" data-testid="deals-funnel-select"><SelectValue placeholder="Embudo"/></SelectTrigger>
                        <SelectContent>{funnels.map(f => <SelectItem key={f.funnel_id} value={f.funnel_id}>{f.name}</SelectItem>)}</SelectContent>
                    </Select>
                    {canManage && <Button onClick={()=>openNew(stages[0]?.stage_id)} data-testid="deals-new-button"><Plus className="w-4 h-4 mr-2"/>Nuevo Deal</Button>}
                </>
            }
        >
            <DragDropContext onDragEnd={onDragEnd}>
                <div className="flex gap-4 overflow-x-auto scrollbar-thin pb-4 h-[calc(100vh-220px)] min-h-[400px]" data-testid="kanban-board">
                    {stages.map((stage) => {
                        const stageDeals = deals.filter(d => d.stage_id === stage.stage_id);
                        const total = stageDeals.reduce((s,d)=>s+(d.amount||0),0);
                        return (
                            <Droppable droppableId={stage.stage_id} key={stage.stage_id}>
                                {(provided, snapshot) => (
                                    <div
                                        className={`min-w-[300px] w-[300px] bg-slate-100 rounded-lg p-3 flex flex-col h-full ${snapshot.isDraggingOver ? "bg-orange-50" : ""}`}
                                        data-testid={`kanban-column-${stage.stage_id}`}
                                    >
                                        <div className="flex items-center justify-between mb-3 px-1 shrink-0">
                                            <div className="flex items-center gap-2">
                                                <div className="w-2 h-2 rounded-full" style={{ background: stage.color }}/>
                                                <h3 className="font-medium text-sm text-slate-800">{stage.name}</h3>
                                                <span className="text-xs text-slate-500">{stageDeals.length}</span>
                                            </div>
                                            <div className="text-xs text-slate-500 font-mono">${total.toLocaleString()}</div>
                                        </div>
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className="space-y-2 flex-1 overflow-y-auto scrollbar-thin pr-1 -mr-1"
                                            data-testid={`kanban-column-scroll-${stage.stage_id}`}
                                        >
                                            {stageDeals.map((d, idx) => (
                                                <Draggable draggableId={d.deal_id} index={idx} key={d.deal_id} isDragDisabled={!canManage}>
                                                    {(prov, snap) => (
                                                        <div
                                                            ref={prov.innerRef}
                                                            {...prov.draggableProps}
                                                            {...prov.dragHandleProps}
                                                            className={`relative bg-white p-3 pb-11 rounded-md border border-slate-200 shadow-sm hover:border-orange-400 transition-colors cursor-grab active:cursor-grabbing select-none ${snap.isDragging ? "ring-2 ring-orange-500" : ""}`}
                                                            style={{ borderLeft: `3px solid ${d.priority==='alta' ? '#DC2626' : d.priority==='media' ? '#D97706' : '#16A34A'}`, ...prov.draggableProps.style }}
                                                            data-testid={`kanban-card-${d.deal_id}`}
                                                        >
                                                            <div>
                                                                <div className="font-medium text-sm text-slate-900">{d.title}</div>
                                                                <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
                                                                    <span className="font-mono">${(d.amount||0).toLocaleString()} {d.currency}</span>
                                                                    <span className="capitalize text-[10px] px-1.5 py-0.5 rounded bg-slate-100">{d.priority}</span>
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                aria-label={`Ver tarjeta ${d.title || "del deal"}`}
                                                                title="Ver tarjeta"
                                                                className="absolute bottom-2.5 right-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:text-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 cursor-pointer"
                                                                onPointerDown={(event) => event.stopPropagation()}
                                                                onClick={()=>openView(d)}
                                                                data-testid={`deal-view-${d.deal_id}`}
                                                            >
                                                                <Eye className="w-4 h-4" aria-hidden="true"/>
                                                            </button>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                            {canManage && <button onClick={()=>openNew(stage.stage_id)} className="w-full text-xs text-slate-500 hover:text-orange-600 py-2 border border-dashed border-slate-300 rounded-md transition-colors">+ Añadir</button>}
                                        </div>
                                    </div>
                                )}
                            </Droppable>
                        );
                    })}
                </div>
            </DragDropContext>

            {/* Deal detail */}
            <Dialog open={Boolean(viewing)} onOpenChange={(value) => !value && setViewing(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    {viewing && (
                        <>
                            <DialogHeader>
                                <DialogTitle>{viewing.title}</DialogTitle>
                                <p className="text-sm text-slate-500">Información completa del deal</p>
                            </DialogHeader>

                            <div className="grid sm:grid-cols-2 gap-3">
                                <DealReadOnlyValue label="Monto" value={`$${(viewing.amount || 0).toLocaleString()} ${viewing.currency || "USD"}`} />
                                <DealReadOnlyValue label="Prioridad" value={viewing.priority || "media"} capitalize />
                                <DealReadOnlyValue label="Empresa" value={companyName(viewing.company_id)} />
                                <DealReadOnlyValue label="Contacto" value={contactName(viewing.contact_id)} />
                                <DealReadOnlyValue label="Embudo" value={currentFunnel?.name || "Sin embudo"} />
                                <DealReadOnlyValue label="Etapa" value={stageName(viewing.stage_id)} />
                            </div>

                            <section className="rounded-lg border border-slate-200 p-4">
                                <h4 className="text-sm font-semibold text-slate-900 mb-2">Notas</h4>
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewing.notes || "Sin notas registradas."}</p>
                            </section>

                            <section className="rounded-lg border border-slate-200 p-4">
                                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-900">
                                    <ListChecks className="w-4 h-4 text-orange-600" />Tareas vinculadas ({dealTasks.length})
                                </div>
                                <div className="space-y-2">
                                    {dealTasks.length === 0 && <p className="text-sm text-slate-400">Sin tareas vinculadas.</p>}
                                    {dealTasks.map((task) => (
                                        <div key={task.task_id} className="flex items-start gap-2 rounded-md bg-slate-50 px-3 py-2">
                                            <CheckSquare className={`w-4 h-4 mt-0.5 shrink-0 ${task.completed ? "text-emerald-500" : "text-slate-300"}`} />
                                            <div className="min-w-0 flex-1">
                                                <p className={`text-sm ${task.completed ? "line-through text-slate-400" : "text-slate-700"}`}>{task.title}</p>
                                                {task.due_date && <p className="text-xs text-slate-400 mt-1">Vence: {new Date(task.due_date).toLocaleString()}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            <DialogFooter>
                                <Button variant="ghost" onClick={() => setViewing(null)}>Cerrar</Button>
                                {canManage && <Button onClick={() => openEdit(viewing)}><Pencil className="w-4 h-4 mr-2" />Editar deal</Button>}
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* New / Edit Deal */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between gap-3">
                            <span>{editing ? "Editar deal" : "Nuevo deal"}</span>
                            {editing && canManageTasks && (
                                <Button size="sm" variant="outline" onClick={openNewTask} data-testid="deal-quick-add-task">
                                    <CheckSquare className="w-4 h-4 mr-2"/>Agregar tarea
                                </Button>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><Label>Título *</Label><Input value={form.title||""} onChange={(e)=>setForm({...form, title: e.target.value})} data-testid="deal-form-title"/></div>
                        <div><Label>Monto</Label><Input type="number" value={form.amount ?? 0} onChange={(e)=>setForm({...form, amount: parseFloat(e.target.value)||0})} data-testid="deal-form-amount"/></div>
                        <div><Label>Moneda</Label><Input value={form.currency||"USD"} onChange={(e)=>setForm({...form, currency: e.target.value})}/></div>
                        <div><Label>Empresa</Label>
                            <CompanySearchSelect
                                value={form.company_id || ""}
                                onChange={(company_id) => {
                                    const selectedContact = contacts.find((c) => c.lead_id === form.contact_id);
                                    setForm({ ...form, company_id, contact_id: selectedContact && selectedContact.company_id !== company_id ? "" : form.contact_id });
                                }}
                                disabled={Boolean(form.contact_id)}
                                data-testid="deal-form-company"
                            />
                        </div>
                        <div><Label>Contacto *</Label>
                            <SearchSelect
                                value={form.contact_id || ""}
                                onChange={(contact_id) => {
                                    const contact = contacts.find((c) => c.lead_id === contact_id);
                                    setForm({ ...form, contact_id, company_id: contact?.company_id || "" });
                                }}
                                options={contacts.filter((c) => !form.company_id || c.company_id === form.company_id).map(c => ({
                                    value: c.lead_id,
                                    label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "Sin nombre",
                                    keywords: `${c.email || ""} ${c.company_name || ""} ${c.phone || ""}`,
                                }))}
                                placeholder="Selecciona contacto"
                                searchPlaceholder="Buscar contacto..."
                                emptyText="Sin contactos encontrados"
                                data-testid="deal-form-contact"
                                footer={canCreateContact ? <Button type="button" variant="ghost" className="w-full justify-start text-orange-600" onClick={() => setContactOpen(true)}><Plus className="mr-2 h-4 w-4"/>Crear nuevo contacto</Button> : null}
                            />
                        </div>
                        <div><Label>Etapa</Label>
                            <Select value={form.stage_id||""} onValueChange={(v)=>setForm({...form, stage_id: v})}>
                                <SelectTrigger><SelectValue/></SelectTrigger>
                                <SelectContent>{stages.map(s => <SelectItem key={s.stage_id} value={s.stage_id}>{s.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Prioridad</Label>
                            <Select value={form.priority||"media"} onValueChange={(v)=>setForm({...form, priority: v})}>
                                <SelectTrigger><SelectValue/></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="alta">Alta</SelectItem>
                                    <SelectItem value="media">Media</SelectItem>
                                    <SelectItem value="baja">Baja</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="col-span-2"><Label>Notas</Label><Textarea value={form.notes||""} onChange={(e)=>setForm({...form, notes: e.target.value})}/></div>
                    </div>

                    {!editing && canManageTasks && (
                        <div className="mt-2 border-t border-slate-100 pt-3">
                            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer select-none" data-testid="deal-inline-task-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={inlineTask.enabled}
                                    onChange={(e) => setInlineTask({ ...inlineTask, enabled: e.target.checked })}
                                    className="rounded"
                                    data-testid="deal-inline-task-toggle"
                                />
                                <ListChecks className="w-4 h-4 text-orange-600" />
                                Agregar una tarea inicial vinculada (opcional)
                            </label>
                            {inlineTask.enabled && (
                                <div className="grid grid-cols-2 gap-3 mt-3" data-testid="deal-inline-task-form">
                                    <div className="col-span-2">
                                        <Label>Título de la tarea *</Label>
                                        <Input
                                            value={inlineTask.title}
                                            onChange={(e) => setInlineTask({ ...inlineTask, title: e.target.value })}
                                            placeholder="Ej. Llamar al cliente, enviar cotización..."
                                            data-testid="deal-inline-task-title"
                                        />
                                    </div>
                                    <div>
                                        <Label>Tipo</Label>
                                        <Select value={inlineTask.type} onValueChange={(v) => setInlineTask({ ...inlineTask, type: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="tarea">Tarea</SelectItem>
                                                <SelectItem value="llamada">Llamada</SelectItem>
                                                <SelectItem value="email">Email</SelectItem>
                                                <SelectItem value="mensaje">Mensaje</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Prioridad</Label>
                                        <Select value={inlineTask.priority} onValueChange={(v) => setInlineTask({ ...inlineTask, priority: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="alta">Alta</SelectItem>
                                                <SelectItem value="media">Media</SelectItem>
                                                <SelectItem value="baja">Baja</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-2">
                                        <Label>Vencimiento</Label>
                                        <Input
                                            type="datetime-local"
                                            value={inlineTask.due_date}
                                            onChange={(e) => setInlineTask({ ...inlineTask, due_date: e.target.value })}
                                            data-testid="deal-inline-task-due"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {editing && (
                        <div className="mt-2 border-t border-slate-100 pt-3">
                            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-700">
                                <ListChecks className="w-4 h-4 text-orange-600"/>Tareas vinculadas ({dealTasks.length})
                            </div>
                            <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="deal-tasks-list">
                                {dealTasks.length === 0 && <div className="text-xs text-slate-400 py-2">Sin tareas. Crea una con el botón "Agregar tarea".</div>}
                                {dealTasks.map(t => (
                                    <div key={t.task_id} className="flex items-center gap-2 text-sm">
                                        <input type="checkbox" checked={!!t.completed} onChange={()=>toggleTask(t)} className="rounded"/>
                                        <span className={t.completed ? "line-through text-slate-400" : "text-slate-700"}>{t.title}</span>
                                        {t.due_date && <span className="text-xs text-slate-400 ml-auto">{new Date(t.due_date).toLocaleDateString()}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <DialogFooter className={editing ? "sm:justify-between" : ""}>
                        {editing && (
                            <Button
                                type="button"
                                variant="destructive"
                                onClick={() => removeDeal(editing)}
                                data-testid={`deal-delete-${editing.deal_id}`}
                            >
                                <Trash2 className="w-4 h-4 mr-2" />Eliminar deal
                            </Button>
                        )}
                        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:ml-auto">
                            <Button variant="ghost" onClick={()=>setOpen(false)}>Cancelar</Button>
                            <Button onClick={save} data-testid="deal-form-save">{editing ? "Guardar cambios" : "Crear deal"}</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ContactFormDialog
                open={contactOpen}
                onOpenChange={setContactOpen}
                onSaved={(contact) => {
                    setContacts((current) => [contact, ...current.filter((item) => item.lead_id !== contact.lead_id)]);
                    setForm((current) => ({ ...current, contact_id: contact.lead_id, company_id: contact.company_id }));
                }}
                users={users}
                canAssignOwner={hasPermission("contacts_global_view")}
                canCreateCompany={canCreateCompany}
                currentUserId={user?.user_id || ""}
            />

            {/* Quick task dialog */}
            <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Nueva tarea {editing ? `· ${editing.title}` : ""}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Título *</Label><Input value={taskForm.title||""} onChange={(e)=>setTaskForm({...taskForm, title: e.target.value})} data-testid="deal-task-form-title"/></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Tipo</Label>
                                <Select value={taskForm.type||"tarea"} onValueChange={(v)=>setTaskForm({...taskForm, type: v})}>
                                    <SelectTrigger><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="tarea">Tarea</SelectItem>
                                        <SelectItem value="llamada">Llamada</SelectItem>
                                        <SelectItem value="reunion">Reunión</SelectItem>
                                        <SelectItem value="email">Email</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div><Label>Prioridad</Label>
                                <Select value={taskForm.priority||"media"} onValueChange={(v)=>setTaskForm({...taskForm, priority: v})}>
                                    <SelectTrigger><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="alta">Alta</SelectItem>
                                        <SelectItem value="media">Media</SelectItem>
                                        <SelectItem value="baja">Baja</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-2"><Label>Vence</Label><Input type="datetime-local" value={taskForm.due_date||""} onChange={(e)=>setTaskForm({...taskForm, due_date: e.target.value})}/></div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={()=>setTaskOpen(false)}>Cancelar</Button>
                        <Button onClick={saveTask} data-testid="deal-task-form-save">Crear tarea</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function DealReadOnlyValue({ label, value, capitalize = false }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <p className="text-xs font-medium text-slate-500">{label}</p>
            <p className={`text-sm font-semibold text-slate-800 mt-1 ${capitalize ? "capitalize" : ""}`}>{value || "—"}</p>
        </div>
    );
}
